"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, ShieldCheck, Compass } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const serviceAreas = [{
  name: "San Francisco County",
  desc: "Expertise in dense urban code compliance, tight-space engineering, and modern city aesthetics."
}, {
  name: "Santa Clara County",
  desc: "Designing and building expansive, high-end outdoor living spaces for Silicon Valley homes."
}, {
  name: "Santa Cruz County",
  desc: "Specialized in coastal weatherproofing, marine-grade materials, and hillside structural integrity."
}, {
  name: "Monterey County",
  desc: "Crafting luxury composite and wood decks that seamlessly blend with rugged, scenic landscapes."
}];
export default function ServingTheNorthernCaliforniaCoast() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="serving-the-northern-california-coast" className="bg-background py-20 md:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={sectionRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div className="relative order-2 lg:order-1">
            <div className="relative aspect-[4/5] md:aspect-square lg:aspect-[4/5] w-full rounded-3xl overflow-hidden bg-muted border border-border/50 shadow-2xl">
              {}
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 mix-blend-overlay pointer-events-none"></div>
              <div className="absolute inset-0 bg-gradient-to-tr from-background/80 via-transparent to-transparent z-10 pointer-events-none"></div>
              
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1631011/img/california_coast_service_map_vector.png" alt="Vector map of Northern California coast showing service areas" className="absolute inset-0 w-full h-full object-cover object-center opacity-90 mix-blend-multiply dark:mix-blend-screen" />

              {}
              <div className="absolute inset-0 z-20 p-8 flex flex-col justify-between pointer-events-none">
                <div className="flex items-center gap-3 bg-background/90 backdrop-blur-sm p-3 rounded-xl shadow-sm border border-border/50 w-max">
                  <Compass className="size-5 text-primary" />
                  <span className="font-default font-semibold text-sm text-foreground tracking-wide uppercase">
                    Service Region
                  </span>
                </div>
                
                <div className="self-end bg-primary text-primary-foreground p-4 rounded-2xl shadow-lg max-w-[200px] backdrop-blur-md bg-primary/95 border border-primary-foreground/10">
                  <p className="font-default font-bold text-2xl mb-1">1990</p>
                  <p className="text-xs opacity-90 leading-tight">Building trust across the coast since 1990.</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="order-1 lg:order-2 flex flex-col justify-center">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/10 border border-secondary/20 mb-6">
                <ShieldCheck className="size-4 text-primary" />
                <span className="text-xs font-semibold tracking-wide text-secondary-foreground uppercase">
                  Licensed General Contractor
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground leading-tight mb-6 tracking-tight">
                Serving the Northern <br className="hidden md:block" />
                <span className="text-primary">California Coast</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-default max-w-xl">
                From the dense urban environments of San Francisco to the rugged coastlines of Monterey, PCH Builders brings over three decades of localized expertise. We navigate complex regional building codes, microclimates, and structural demands to deliver flawless deck installations.
              </p>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 mt-4">
              {serviceAreas.map((area, index) => <Card key={area.name} data-index={index} className="bg-card/40 backdrop-blur-sm border-border/60 shadow-sm hover:shadow-md hover:border-primary/40 transition-all duration-300 group">
                  <CardHeader className="p-5 pb-2">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 bg-background p-2 rounded-lg border border-border/50 group-hover:bg-primary/10 group-hover:border-primary/30 transition-colors">
                        <MapPin className="size-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg font-bold font-default text-foreground leading-tight">
                        {area.name}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="p-5 pt-2">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {area.desc}
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}